import {
  GIT_CR_SUMMARY_BODY_MAX,
  type GitCrSummaryState,
  type GitCrSummaryView,
  SUMMARIZE_CHANGE_REQUEST_SKILL,
} from '@apm/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { NotebookPen } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { ErrorState, Skeleton } from '../../../components/ui/states';
import { displayDateTime } from '../../../lib/date';
import { errorMessage } from '../../../lib/error-message';
import { AiAction } from '../../ai/components/ai-action';
import { useTrackedAiRun } from '../../ai/use-ai-run';
import { gitCrSummaryApi, gitCrSummaryKeys } from '../api';
import { shortSha } from '../git-format';
import { type GitChangeRequestDrawerSlotProps } from '../types';

/** Màu của nhãn trạng thái — viết sẵn cả bảng (Tailwind không thấy lớp ghép lúc chạy). */
const STATUS_CLASS: Record<GitCrSummaryView['status'], string> = {
  ai_suggested: 'border-rag-amber/60 bg-rag-amber/10 text-foreground',
  approved: 'border-rag-green/60 bg-rag-green/10 text-foreground',
  manual: 'border-border bg-muted/60 text-foreground',
};

/**
 * Lời tóm tắt MR trên ngăn bên — APM-GIT-m, docs/plan-git-integration.md §8.5.
 * Cắm vào `GIT_CHANGE_REQUEST_DRAWER_SLOTS`.
 *
 * - Nhãn nói lời tóm tắt **dựng trên gì**: "Có đọc mã" chỉ khi lượt chạy thật sự
 *   đọc diff (kho đã bật `aiReadDiff`), còn lại "Dựa trên lời mô tả, không đọc
 *   mã" — đặt từ kết quả tool, không do model tự khai.
 * - Bản AI ở trạng thái **chờ duyệt** tới khi người có `git:act` bấm Duyệt (luật
 *   #6); sửa hay viết tay ⇒ "Viết tay".
 * - Chưa có key AI ⇒ `AiAction` tự đổi thành lối làm tay; nút "Viết tay" luôn có
 *   (đường làm tay của skill — `manual`).
 *
 * Nhận `asOf` (luật #7): xem quá khứ thì đọc lời tóm tắt **tại mốc ấy** và chỉ
 * đọc — không chạy AI, không duyệt, không sửa lịch sử.
 */
export function GitCrSummarySlot({
  projectId,
  detail,
  asOf,
  permissions,
}: GitChangeRequestDrawerSlotProps): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const track = useTrackedAiRun();
  const { repositoryId, number } = detail.changeRequest;
  const [draft, setDraft] = useState<string | null>(null);

  const key = gitCrSummaryKeys.state(projectId, repositoryId, number, asOf);
  const state = useQuery({
    queryKey: key,
    queryFn: () => gitCrSummaryApi.state(projectId, repositoryId, number, asOf),
    retry: false,
  });

  const store = (next: GitCrSummaryState): void => {
    queryClient.setQueryData(key, next);
  };

  const run = useMutation({
    mutationFn: (refresh: boolean) =>
      track(
        {
          skill: SUMMARIZE_CHANGE_REQUEST_SKILL,
          label: t('gitTab.crSummary.trackLabel', { number }),
          describe: (result: GitCrSummaryState) => result.summary?.body ?? '',
        },
        (runId) => gitCrSummaryApi.run(projectId, repositoryId, number, { runId, refresh }),
      ),
    onSuccess: (next) => {
      store(next);
      toast.success(t('gitTab.crSummary.done'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const approve = useMutation({
    mutationFn: () => gitCrSummaryApi.approve(projectId, repositoryId, number),
    onSuccess: (next) => {
      store(next);
      toast.success(t('gitTab.crSummary.approvedToast'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const write = useMutation({
    mutationFn: (body: string) => gitCrSummaryApi.write(projectId, repositoryId, number, { body }),
    onSuccess: (next) => {
      store(next);
      setDraft(null);
      toast.success(t('gitTab.crSummary.saved'));
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const readOnly = asOf !== undefined;
  const summary = state.data?.summary ?? null;
  const outdated = state.data?.outdated ?? null;
  const busy = run.isPending || approve.isPending || write.isPending;

  return (
    <section aria-label={t('gitTab.crSummary.title')} data-testid="git-cr-summary">
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-xs font-medium text-muted-foreground">{t('gitTab.crSummary.title')}</h3>
        {!readOnly && draft === null ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {summary === null || summary.status === 'ai_suggested' ? (
              <AiAction
                skill={SUMMARIZE_CHANGE_REQUEST_SKILL}
                label={summary === null ? t('gitTab.crSummary.run') : t('gitTab.crSummary.rerun')}
                icon={NotebookPen}
                size="sm"
                variant="outline"
                pending={run.isPending}
                disabled={busy}
                onRun={() => run.mutate(summary !== null)}
                routeParams={{ projectId }}
              />
            ) : null}
            {permissions.act ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => setDraft(summary?.body ?? '')}
              >
                {summary === null ? t('gitTab.crSummary.writeManual') : t('gitTab.crSummary.edit')}
              </Button>
            ) : null}
          </div>
        ) : null}
      </header>

      {state.isPending ? (
        <Skeleton className="h-16" />
      ) : state.isError ? (
        <ErrorState message={errorMessage(state.error, t)} onRetry={() => void state.refetch()} />
      ) : draft !== null ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft.trim().length > 0) write.mutate(draft);
          }}
        >
          <textarea
            rows={4}
            value={draft}
            maxLength={GIT_CR_SUMMARY_BODY_MAX}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('gitTab.crSummary.placeholder')}
            aria-label={t('gitTab.crSummary.title')}
            className="rounded-[12px] border border-border bg-background p-2 text-sm text-foreground"
          />
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={busy || draft.trim().length === 0}>
              {t('gitTab.crSummary.save')}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setDraft(null)}>
              {t('gitTab.crSummary.cancel')}
            </Button>
          </div>
        </form>
      ) : summary !== null ? (
        <SummaryCard
          summary={summary}
          footer={
            !readOnly && summary.status === 'ai_suggested' && permissions.act ? (
              <Button
                type="button"
                size="sm"
                className="self-start"
                disabled={busy}
                onClick={() => approve.mutate()}
              >
                {t('gitTab.crSummary.approve')}
              </Button>
            ) : null
          }
        />
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xs text-muted-foreground" data-testid="git-cr-summary-empty">
            {readOnly ? t('gitTab.crSummary.pastEmpty') : t('gitTab.crSummary.empty')}
          </p>
          {outdated ? (
            <div className="opacity-70">
              <p className="mb-1 text-xs text-muted-foreground">
                {t('gitTab.crSummary.outdated', { sha: shortSha(outdated.headSha) })}
              </p>
              <SummaryCard summary={outdated} footer={null} />
            </div>
          ) : null}
        </div>
      )}
    </section>
  );
}

/** Một lời tóm tắt: ba câu, nhãn trạng thái, nhãn "đọc mã hay không". Thuần trình bày. */
function SummaryCard({
  summary,
  footer,
}: {
  readonly summary: GitCrSummaryView;
  readonly footer: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border px-3 py-2">
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <span
          className={`rounded-full border px-2 py-0.5 ${STATUS_CLASS[summary.status]}`}
          data-testid="git-cr-summary-status"
        >
          {t(`gitTab.crSummary.status.${summary.status}`)}
        </span>
        {summary.status !== 'manual' || summary.readDiff ? (
          <span
            className="rounded-full border border-border px-2 py-0.5 text-muted-foreground"
            data-testid="git-cr-summary-basis"
          >
            {summary.readDiff
              ? t('gitTab.crSummary.basis.diff')
              : t('gitTab.crSummary.basis.description')}
          </span>
        ) : null}
        <span className="text-muted-foreground">{displayDateTime(summary.updatedAt)}</span>
      </div>
      <ul className="flex flex-col gap-1 text-sm">
        {summary.body
          .split('\n')
          .filter((line) => line.trim().length > 0)
          .map((line, index) => (
            <li key={index} className="break-words">
              {line}
            </li>
          ))}
      </ul>
      {footer}
    </div>
  );
}
